import { cn } from '@/lib/utils'
import type { EvaluationTemplate } from '@/lib/types'

/**
 * The maturity scale a template is scored on, rendered as a legend:
 * `1 Initial · 2 Emergent · 3 Structured · 4 Integrated · 5 Optimized`.
 *
 * Colors come from the 5-step ordinal ramp in index.css. The ramp has exactly
 * five steps, but a template's scale doesn't have to — `stepClass` maps any
 * scale length onto the ramp proportionally, so a 1–3 or 1–7 template still gets
 * a sensible light→dark progression.
 */

/** Which ramp step (1–5) a scale position maps to. `i` is 0-based. */
export function stepClass(i: number, count: number): string {
  if (count <= 1) return 'scale-3'
  const step = Math.round((i / (count - 1)) * 4) + 1
  return `scale-${step}`
}

/** Every point on a template's scale, as {score, label} pairs. */
export function scalePoints(
  template: Pick<EvaluationTemplate, 'scale_min' | 'scale_max' | 'scale_labels'>,
): Array<{ score: number; label: string | null }> {
  const out: Array<{ score: number; label: string | null }> = []
  for (let v = template.scale_min; v <= template.scale_max; v++) {
    out.push({ score: v, label: template.scale_labels[v - template.scale_min] ?? null })
  }
  return out
}

/** The name for a score, e.g. 4 → "Integrated". Rounds a fractional average. */
export function scaleLabelFor(
  template: Pick<EvaluationTemplate, 'scale_min' | 'scale_max' | 'scale_labels'>,
  score: number | null | undefined,
): string | null {
  if (score == null) return null
  const i = Math.round(score) - template.scale_min
  return template.scale_labels[i] ?? null
}

export function ScaleLegend({
  template,
  className,
  /** Highlight one point — used to show where a score or average sits. */
  active,
}: {
  template: Pick<EvaluationTemplate, 'scale_min' | 'scale_max' | 'scale_labels'>
  className?: string
  active?: number | null
}) {
  const points = scalePoints(template)
  if (points.length === 0 || points.every((p) => !p.label)) return null

  // Five cells of "Initial → Optimized" need the room; eleven cells of "40%" do
  // not, and at the wide setting they scroll sideways for no reason.
  const cell = points.length > 7 ? 'min-w-[3.25rem]' : 'min-w-[5.25rem]'

  const activeStep = active == null ? null : Math.round(active)

  return (
    <div
      // min-w-0 keeps the min-w-max row below from widening whatever contains
      // the legend — in a dialog that would push the whole form out of the box.
      className={cn('min-w-0 overflow-x-auto', className)}
      role="img"
      aria-label={
        'Scale: ' + points.map((p) => `${p.score} ${p.label ?? ''}`.trim()).join(', ')
      }
    >
      <div className="flex min-w-max overflow-hidden rounded-md border">
        {points.map((p, i) => (
          <div
            key={p.score}
            className={cn(
              stepClass(i, points.length),
              cell,
              'flex flex-1 flex-col items-center gap-0.5 px-2 py-2 text-center',
              i > 0 && 'border-l',
              // The highlighted cell gets a ring rather than a different color —
              // changing its color would break the ramp's "more than" reading.
              activeStep === p.score && 'ring-2 ring-inset ring-foreground/60',
            )}
            style={{
              background: 'hsl(var(--scale-bg))',
              color: 'hsl(var(--scale-ink))',
              borderColor: 'hsl(var(--scale-ink) / 0.25)',
            }}
          >
            <span className="text-sm font-bold leading-none tabular-nums">{p.score}</span>
            {p.label && <span className="text-[11px] font-medium leading-tight">{p.label}</span>}
          </div>
        ))}
      </div>
    </div>
  )
}

/** A single scale point inline — the swatch plus its name. */
export function ScaleChip({
  template,
  score,
  className,
}: {
  template: Pick<EvaluationTemplate, 'scale_min' | 'scale_max' | 'scale_labels'>
  score: number
  className?: string
}) {
  const count = template.scale_max - template.scale_min + 1
  const label = scaleLabelFor(template, score)
  return (
    <span
      className={cn(
        stepClass(Math.round(score) - template.scale_min, count),
        'inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-xs font-medium',
        className,
      )}
      style={{ background: 'hsl(var(--scale-bg))', color: 'hsl(var(--scale-ink))' }}
    >
      <span className="tabular-nums">{score}</span>
      {label && <span>{label}</span>}
    </span>
  )
}
